import {LitElement, html, css, nothing} from 'lit';
import {customElement, state} from 'lit/decorators.js';
import global_css from './global-css.js';
import Decimal, {DecimalSource} from './break_infinity.js';

import {UnlockableLitElement} from './unlockable-lit-element.js';
import {
  fancy,
  formatDelta,
  formatResourceCapacity,
  formatResourceValue,
} from './utils.js';
import tippy, {Instance as TippyInstance} from 'tippy.js';
import {ri} from './runner-idle.js';
import {Activity} from './activity.js';
import {Modifier} from './modifier.js';
import {unsafeHTML} from 'lit/directives/unsafe-html.js';
import {ResourceName, ResourceSaveData} from './types/runner-idle.js';
import {BehaviorSubject, of} from 'rxjs';
import {ValuefulUnlockableLitElement} from './valueful-unlockable-lit-element.js';
import {observe} from './directives/observe.js';

/**
 * A resource; e.g. Stamina
 *
 */
@customElement('ri-resource')
export class Resource extends ValuefulUnlockableLitElement {
  // #region Properties (11)

  public static override styles = [
    global_css,
    css`
      :host {
        transition: all 0.35s ease;
      }
      .ri-resource {
        display: flex;
        flex-direction: row;
        background: rgb(var(--value-color), 0.8);
        justify-content: space-between;
        width: 100%;
      }
      // shadow DOM css above overrides div[hidden]{display:none;}
      .ri-resource[hidden] {
        display: none;
      }
      .ri-resource-label,
      .ri-resource-meter {
        display: flex;
        align-items: center;
        justify-content: space-between;
      }
      .ri-resource-label {
        width: 50%;
      }
      .ri-resource-meter {
        width: 50%;
      }
      smooth-progress {
        height: 20px;
        width: 100%;
      }
      .delta {
        min-width: 48px;
        display: flex;
        justify-content: flex-end;
      }
      .whitespace {
        white-space: pre;
      }
      .expanded-indicator {
        padding: 4px;
      }

      .details-container {
        display: flex;
        transition: all 0.35s ease;
      }

      .details-container * {
        pointer-events: none;
        font-size: 0.8rem;
      }

      .details-description {
        flex: 1;
      }
      .details-capacity {
        flex: 1;
        /* background-color: rgb(var(--value-color), 0.6); */
      }
    `,
  ];

  @state() public delta = new Decimal(0);
  public static create(overrides: Partial<Resource>) {
    const instance = document.createElement('ri-resource');
    instance.setup(overrides);
    instance.baseValue$.next(new Decimal(overrides.value ?? instance.value));
    return instance;
  }

  public override baseTags = new Set(['resource']);
  public description = 'Description';
  public override displayName = 'nothing';
  public expanded = false;
  public fullHeight = '';
  public override name: ResourceName = 'stamina';
  public onTick: (performed: boolean, activity: Activity) => void = () => {};
  public tooltip: TippyInstance;

  public get valueHTML() {
    return `<span class='${
      this.name
    } fancy resource-value'>${this.valueFormatter(this.value)}</span> ${
      this.displayName
    }`;
  }

  // #endregion Properties (11)

  // #region Constructors (1)

  constructor() {
    super();
    this.tooltip = tippy(this, {
      placement: 'top-start',
      allowHTML: true,
    });
    let canceled = false;
    this.addEventListener('mouseenter', () => {
      if (ri.settings.resources.expandOnHover) {
        canceled = true;
        this.toggleExpand(true);
      }
    });
    this.addEventListener('mouseleave', () => {
      if (ri.settings.resources.expandOnHover) {
        canceled = false;
        setTimeout(() => {
          if (!canceled) {
            this.toggleExpand(false);
          }
        }, 100);
      }
    });
    this.addEventListener('click', () => {
      if (!ri.settings.resources.expandOnHover) {
        this.toggleExpand();
      }
    });
    setTimeout(() => {
      this.toggleExpand(true);
      this.toggleExpand(false);
    }, 1000);
  }

  // #endregion Constructors (1)

  // #region Public Methods (3)

  public fancy(value: Decimal) {
    return fancy(this.name, this.valueFormatter(value));
  }

  public override render() {
    if (ri.settings.resources.expandOrTooltip) {
      this.tooltip.disable();
    } else {
      this.tooltip.enable();
    }
    setTimeout(() => {
      this.tooltip.setContent(
        `${this.description}${
          this.capacityModifiers.length > 0
            ? `<br><br><span class='whitespace'>${this.capacityString()}</span>`
            : ''
        }`
      );
    });

    return html`
      <div class="ri-resource">
        <div class="ri-resource-label">
          <div class="flex-horizontal flex-centered">
            <div class="expanded-indicator">${this.expanded ? '▾' : '▸'}</div>
            <span class="fancy"
              >${this.level.gt(1)
                ? html`<span>LVL${this.level}</span> `
                : ''}${this.displayName}</span
            >
          </div>
        </div>
        <div class="ri-resource-meter">
          <smooth-progress
            class="value"
            .value=${this.value}
            .max=${new Decimal(this.capacity)}
            .min=${new Decimal(this.minimum)}
            .text=${`${this.valueFormatter(this.value)} /
              ${this.capacityFormatter(this.capacity)}`}
          ></smooth-progress>
          <div class="delta">${formatDelta(this.delta, 0)}</div>
        </div>
      </div>
      <div class="details-container">
        <div class="details-description">${unsafeHTML(this.description)}</div>
        ${this.capacityModifiers.length > 0
          ? html` <div class="details-capacity">
              ${unsafeHTML(`<span>${this.capacityString()}</span>`)}
            </div>`
          : nothing}
      </div>
    `;
  }

  public toggleExpand(expand?: boolean) {
    // return;
    const oldState = this.expanded;
    this.expanded =
      ri.settings.resources.expandOrTooltip &&
      (ri.settings.resources.keepExpandedAndAutoExpand ||
        (expand ?? !this.expanded));
    const detailsContainer = this.shadowRoot?.querySelector(
      '.details-container'
    ) as HTMLDivElement;
    if (!detailsContainer) return;
    if (this.expanded && !oldState) {
      setTimeout(() => {
        if (!this.fullHeight) {
          if (this.hidden) {
            this.hidden = false;
            this.fullHeight = `${20 + this.offsetHeight}px`;
            this.hidden = true;
          } else {
            this.fullHeight = this.offsetHeight + 'px';
          }
        }
        // this.style.minHeight = this.fullHeight;
        detailsContainer.style.opacity = '1';
        detailsContainer.style.maxHeight = '0px';
        setTimeout(() => {
          if (this.expanded) detailsContainer.style.maxHeight = '100%';
        }, 350);
        detailsContainer.hidden = false;
      });
    } else if (!this.expanded) {
      setTimeout(() => {
        // this.style.minHeight = '20px';
        detailsContainer.style.opacity = '0';
        detailsContainer.style.maxHeight = '0px';
        detailsContainer.hidden = true;
      });
    }
    this.requestUpdate();
  }

  // #endregion Public Methods (3)
}

@customElement('ri-meter')
export class Meter extends LitElement {
  // #region Properties (4)

  public static override styles = css`
    meter::-webkit-meter-optimum-value {
      background: var(--color, green);
    }
    meter::-webkit-meter-bar {
      background: rgb(var(--none-color, none), 1);
      height: var(--height, 16px);
    }
    meter {
      height: var(--height, 16px);
      width: var(--width, 128px);
      vertical-align: baseline;
    }
  `;

  @state() public capacity = new Decimal(0);
  @state() public minimum = new Decimal(0);
  @state() public value = new Decimal(0);

  // #endregion Properties (4)

  // #region Public Methods (1)

  public override render() {
    return html`
      <meter
        value=${this.value.toNumber()}
        max=${this.capacity.toNumber()}
        min=${this.minimum.toNumber()}
      ></meter>
    `;
  }

  // #endregion Public Methods (1)
}
